import * as queryString from 'qs';
import API_BASE_URL from '../../../shared/server/apiConfig';
import { perror } from '../../../shared/utils/plog';
import request, { downloadFile } from './apiUtils';
import RALPH_API_BASE_URL from './ralphApi';

export async function fetchAvailableStatements(filters) {
  const filtersString = filters ? queryString.stringify(filters, { skipNulls: true, indices: false }) : '';
  const SEARCH_URL = `${API_BASE_URL}/reports/available-statements?${filtersString}`;
  return request(SEARCH_URL, { method: 'GET' })
    .then((result) => result.json())
    .catch((error) => {
      perror(error);
      return error;
    });
}

export async function fetchAdminStatements(filters) {
  const queryParams = queryString.stringify(filters, { skipNulls: true, indices: false });
  const SEARCH_URL = `${API_BASE_URL}/reports/statement/admin?${queryParams}`;
  return request(SEARCH_URL, { method: 'GET' })
    .then((result) => result.json())
    .catch((error) => {
      perror(error);
      return error;
    });
}

export async function fetchStatementsForDoctor(filters) {
  const queryParams = queryString.stringify(filters, { skipNulls: true, indices: false });
  const SEARCH_URL = `${API_BASE_URL}/reports/statement/doctor?${queryParams}`;
  return request(SEARCH_URL, { method: 'GET' })
    .then((result) => result.json())
    .catch((error) => {
      perror(error);
      return error;
    });
}

export async function fetchStatementsForPool(filters) {
  const queryParams = queryString.stringify(filters, { skipNulls: true, indices: false });
  const SEARCH_URL = `${API_BASE_URL}/reports/statement/pool?${queryParams}`;
  return request(SEARCH_URL, { method: 'GET' })
    .then((result) => result.json())
    .catch((error) => {
      perror(error);
      return error;
    });
}

export async function fetchPaymentRequestForDoctor(filters) {
  const filtersString = queryString.stringify(filters, { skipNulls: true, indices: false });
  const SEARCH_URL = `${API_BASE_URL}/reports/payment-request/doctor?${filtersString}`;
  return request(SEARCH_URL, { method: 'GET' })
    .then((result) => result.json())
    .catch((error) => {
      perror(error);
      return error;
    });
}

export async function fetchPaymentRequestForPool(groupId, periodStart) {
  const filtersString = queryString.stringify({ periodStart }, { skipNulls: true, indices: false });
  const SEARCH_URL = `${RALPH_API_BASE_URL}/activities/groups/${groupId}/payment-requests-by-service-date?${filtersString}`;
  return request(SEARCH_URL, { method: 'GET' })
    .then((result) => result.json())
    .catch((error) => {
      perror(error);
      return error;
    });
}

export async function generatePeriodReportLinks(params: {
  userId: string;
  statementDate: number;
  periodStart: number;
  periodEnd: number;
}) {
  const PAYMENT_REQUEST_BY_SENT_DATE_URL = `${RALPH_API_BASE_URL}/reporting?${queryString.stringify(
    {
      slug: 'payment-requests-by-sent-date',
      userId: params.userId,
      periodStart: params.periodStart,
      periodEnd: params.periodEnd,
      mode: 'share'
    },
    { skipNulls: true, indices: false }
  )}`;
  const STATEMENT_URL = `${RALPH_API_BASE_URL}/reporting?${queryString.stringify(
    {
      slug: 'statement',
      userId: params.userId,
      statementDate: params.statementDate,
      restrict: 'personal',
      mode: 'share'
    },
    { skipNulls: true, indices: false }
  )}`;

  return Promise.all([
    request(PAYMENT_REQUEST_BY_SENT_DATE_URL, {
      method: 'GET'
    })
      .then((result) => result.json())
      .catch((error) => {
        perror(error);
        return error;
      }),
    request(STATEMENT_URL, {
      method: 'GET'
    })
      .then((result) => result.json())
      .catch((error) => {
        perror(error);
        return error;
      })
  ]);
}

export default class ReportAPI {
  static async downloadDoctorOutsideRAMQReport({
    groupId = undefined,
    userId,
    year,
    mode = 'pdf'
  }: {
    groupId?: string;
    userId: string;
    year: string;
    mode?: 'pdf' | 'html';
  }) {
    const params = { slug: 'payment-requests-outside-ramq', userId, year, mode };

    if (groupId) {
      Object.assign(params, { groupId });
    }

    const PAYMENT_REQUEST_BY_CATEGORY_URL = `${RALPH_API_BASE_URL}/reporting?${queryString.stringify(params)}`;
    const response = await request(PAYMENT_REQUEST_BY_CATEGORY_URL, { method: 'GET' });

    if (mode === 'pdf') {
      return downloadFile(response);
    }

    return response.text();
  }

  static async downloadPoolOutsideRAMQReport({ poolNumber, year }) {
    const queryParams = queryString.stringify({ poolNumber, year });
    const PAYMENT_REQUEST_BY_CATEGORY_URL = `${API_BASE_URL}/reports/outside-ramq-requests?${queryParams}`;
    const response = await request(PAYMENT_REQUEST_BY_CATEGORY_URL, { method: 'GET' });

    return downloadFile(response);
  }

  static async downloadReceivableAccounts({
    groupId,
    userId,
    date,
    mode = 'pdf'
  }: {
    mode?: 'pdf' | 'html';
    groupId?: string;
    userId: string;
    date: number;
  }) {
    const queryParams = queryString.stringify({ slug: 'receivable-accounts', mode, userId, date, groupId });
    const response = await request(`${RALPH_API_BASE_URL}/reporting?${queryParams}`, { method: 'GET' });

    if (mode === 'pdf') {
      return downloadFile(response);
    }

    return response.text();
  }

  static async downloadTravelDistancesReport({
    userId,
    periodStart,
    periodEnd,
    mode = 'pdf'
  }: {
    mode?: 'pdf' | 'html';
    userId: string;
    periodStart: number;
    periodEnd: number;
  }) {
    const queryParams = queryString.stringify({
      slug: 'travel-distance-summary-by-period',
      mode,
      userId,
      periodStart,
      periodEnd
    });
    const response = await request(`${RALPH_API_BASE_URL}/reporting?${queryParams}`, { method: 'GET' });

    if (mode === 'pdf') {
      return downloadFile(response);
    }
    return response.text();
  }

  static async downloadActivitiesSearch(filters) {
    const filtersString = queryString.stringify(
      { slug: 'search-activities', ...filters, mode: 'pdf' },
      { skipNulls: true, indices: false }
    );
    const SEARCH_URL = `${RALPH_API_BASE_URL}/reporting?${filtersString}`;
    const response = await request(SEARCH_URL, { method: 'GET' });

    return downloadFile(response);
  }
}
